import React, { useEffect, useState } from "react";
import { api, formatErr, MAPBOX_TOKEN } from "@/lib/api";
import { geocodeItalianAddress } from "@/lib/geocode";
import { PageHeader } from "@/components/AppShell";
import { STATUS_LABELS, STATUS_COLORS, fmtNum } from "@/lib/format";
import { Plus, Trash2, MapPinned, Mail, Phone, Edit3, ArrowLeft, User, Building2, Zap, Percent, MapPin, Sparkles, Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const empty = {
  type: "private", first_name: "", last_name: "", business_name: "", email: "", phone: "",
  address: "", city: "", province: "", postal_code: "",
  latitude: null, longitude: null,
  annual_consumption_kwh: 3500, energy_price_per_kwh: 0.28, meter_power_kw: 3,
  meter_phase: "monofase",
  fiscal_deduction_pct: 50, incentive_pct: 0, incentive_label: "", amortization_years: 0,
  incentives: [],
  interest_battery: false, interest_ev: false, status: "new_lead",
};

const isBusiness = (t) => t === "business" || t === "condominium" || t === "public";
const TYPE_LABELS = { private: "Privato", business: "Azienda", condominium: "Condominio", public: "Ente pubblico" };

const KIND_LABELS = { detrazione: "Detrazione %", fondo_perduto: "Fondo perduto %", importo: "Importo €" };
const PRESETS_PRIVATE = [
  { label: "Detrazione ristrutturazione 50%", kind: "detrazione", pct: 50, years: 10 },
  { label: "Detrazione 36%", kind: "detrazione", pct: 36, years: 10 },
];
const PRESETS_BUSINESS = [
  { label: "Bando / fondo perduto", kind: "fondo_perduto", pct: 30 },
  { label: "Credito d'imposta beni 4.0", kind: "detrazione", pct: 6, years: 5 },
];

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : "i" + Math.random().toString(36).slice(2));

// Garantisce form.incentives: se assente, lo deriva dai campi legacy
function withIncentives(c) {
  if (Array.isArray(c.incentives) && c.incentives.length) return c;
  const inc = [];
  if (!isBusiness(c.type) && c.fiscal_deduction_pct > 0) {
    inc.push({ id: newId(), label: `Detrazione ${c.fiscal_deduction_pct}%`, kind: "detrazione", pct: c.fiscal_deduction_pct, years: 10, enabled: true });
  }
  if (isBusiness(c.type) && c.incentive_pct > 0) {
    inc.push({ id: newId(), label: c.incentive_label || "Fondo perduto", kind: "fondo_perduto", pct: c.incentive_pct, enabled: true });
  }
  return { ...c, incentives: inc };
}

export default function Customers() {
  const [list, setList] = useState([]);
  const [mode, setMode] = useState("list");        // list | edit
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const reload = () => api.get("/customers").then((r) => setList(r.data));
  useEffect(() => { reload(); }, []);

  const counts = list.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});
  const filtered = filter === "all" ? list : list.filter((c) => c.status === filter);

  const openNew = () => { setForm({ ...empty, incentives: [] }); setEditingId(null); setMode("edit"); window.scrollTo({ top: 0 }); };
  const openEdit = (c) => { setForm(withIncentives({ ...empty, ...c })); setEditingId(c.id); setMode("edit"); window.scrollTo({ top: 0 }); };
  const closeEditor = () => { setMode("list"); setForm(empty); setEditingId(null); };

  const save = async () => {
    setBusy(true);
    try {
      let payload = { ...form };
      // mantiene allineati i campi legacy per compatibilità
      const firstDetr = (payload.incentives || []).find((i) => i.enabled && i.kind === "detrazione");
      const firstBonus = (payload.incentives || []).find((i) => i.enabled && i.kind === "fondo_perduto");
      payload.fiscal_deduction_pct = firstDetr ? Number(firstDetr.pct) || 0 : 0;
      payload.incentive_pct = firstBonus ? Number(firstBonus.pct) || 0 : 0;
      payload.incentive_label = firstBonus ? firstBonus.label : "";

      const missingCoords = payload.latitude == null || payload.longitude == null;
      const hasAddressHint = Boolean(payload.address?.trim() || payload.city?.trim());
      let geocoded = false;
      if (missingCoords && hasAddressHint && MAPBOX_TOKEN) {
        const g = await geocodeItalianAddress(payload);
        if (g) { payload = { ...payload, latitude: g.latitude, longitude: g.longitude }; geocoded = true; }
      }
      if (editingId) await api.patch(`/customers/${editingId}`, payload);
      else await api.post("/customers", payload);
      let msg = editingId ? "Cliente aggiornato" : "Cliente creato";
      if (geocoded) msg += " · coordinate da indirizzo";
      toast.success(msg);
      closeEditor(); reload();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const geocodeNow = async () => {
    if (!form.address?.trim() && !form.city?.trim()) { toast.error("Inserisci almeno indirizzo o città"); return; }
    if (!MAPBOX_TOKEN) { toast.error("Token Mapbox mancante (REACT_APP_MAPBOX_TOKEN)"); return; }
    const g = await geocodeItalianAddress(form);
    if (!g) { toast.error("Indirizzo non trovato: prova a essere più specifico (via, CAP, città)"); return; }
    setForm((f) => ({ ...f, latitude: g.latitude, longitude: g.longitude }));
    toast.success("Coordinate aggiornate");
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il cliente?")) return;
    await api.delete(`/customers/${id}`); reload();
  };

  if (mode === "edit") {
    return <CustomerEditor form={form} setForm={setForm} editingId={editingId} busy={busy} save={save} cancel={closeEditor} geocodeNow={geocodeNow} />;
  }

  return (
    <div data-testid="page-customers">
      <PageHeader eyebrow="CRM" title="Clienti" subtitle={`${list.length} contatti totali`} actions={
        <button onClick={openNew} className="px-4 py-2.5 bg-zinc-900 text-white font-semibold rounded-xl text-sm flex items-center gap-2" data-testid="new-customer-btn">
          <Plus size={14} /> Nuovo cliente
        </button>
      }/>

      <div className="px-8 py-6">
        <div className="flex flex-wrap gap-2 mb-5" data-testid="customer-filters">
          {[["all", "Tutti", list.length], ...Object.entries(STATUS_LABELS).map(([k, v]) => [k, v, counts[k] || 0])]
            .filter(([k, , n]) => k === "all" || n > 0)
            .map(([k, label, n]) => {
              const active = filter === k;
              return (
                <button key={k} onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200/70 hover:border-zinc-300"}`}
                  data-testid={`filter-${k}`}>
                  {label}<span className={`font-mono text-[10px] ${active ? "text-zinc-300" : "text-zinc-400"}`}>{n}</span>
                </button>
              );
            })}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center text-zinc-500 py-16 bg-white border border-zinc-200/70 rounded-2xl">{list.length === 0 ? "Nessun cliente. Crea il primo." : "Nessun cliente con questo stato."}</div>
        ) : (
          <div className="bg-white border border-zinc-200/70 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="customers-table">
                <thead>
                  <tr className="border-b border-zinc-200/70 bg-zinc-50/60">
                    {["Cliente", "Tipo", "Località", "Consumo", "Stato", ""].map((h, i) => (
                      <th key={i} className={`font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-medium py-3 px-4 ${i === 3 ? "text-right" : "text-left"} ${i === 5 ? "text-right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const name = c.business_name || `${c.first_name} ${c.last_name}`.trim();
                    const biz = isBusiness(c.type);
                    const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
                    return (
                      <tr key={c.id} onClick={() => navigate(`/app/customers/${c.id}`)}
                        className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/70 cursor-pointer transition-colors" data-testid="customer-row">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`w-9 h-9 shrink-0 rounded-full border flex items-center justify-center ${biz ? "bg-sky-50 border-sky-200 text-sky-600" : "bg-amber-50 border-amber-200 text-amber-600"}`}>
                              {biz ? <Building2 size={15}/> : <User size={15}/>}
                            </span>
                            <div className="min-w-0">
                              <div className="font-medium text-zinc-900 truncate">{name || "Senza nome"}</div>
                              <div className="text-xs text-zinc-400 flex flex-wrap gap-x-3 gap-y-0.5">
                                {c.email && <span className="flex items-center gap-1 truncate"><Mail size={10}/>{c.email}</span>}
                                {c.phone && <span className="flex items-center gap-1"><Phone size={10}/>{c.phone}</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${biz ? "bg-sky-50 text-sky-600" : "bg-amber-50 text-amber-600"}`}>{TYPE_LABELS[c.type] || c.type}</span>
                        </td>
                        <td className="py-3 px-4 text-zinc-600">
                          {c.city ? <span className="flex items-center gap-1.5"><MapPinned size={12} className="text-zinc-400"/>{c.city}{c.province ? ` (${c.province})` : ""}</span> : <span className="text-zinc-300">—</span>}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-amber-600 whitespace-nowrap">{fmtNum(c.annual_consumption_kwh)} <span className="text-zinc-400 text-xs">kWh</span></td>
                        <td className="py-3 px-4">
                          <span className="font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full whitespace-nowrap" style={{ color: STATUS_COLORS[c.status], backgroundColor: STATUS_COLORS[c.status] + "14" }}>
                            {STATUS_LABELS[c.status] || c.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1 justify-end">
                            <Link to={`/app/simulator?customer=${c.id}`} onClick={(e) => e.stopPropagation()} className="p-2 hover:bg-zinc-100 rounded-lg text-sky-600" title="Simula" data-testid={`simulate-${c.id}`}><MapPinned size={15} /></Link>
                            <button onClick={stop(() => openEdit(c))} className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-500" title="Modifica" data-testid={`edit-${c.id}`}><Edit3 size={15} /></button>
                            <button onClick={stop(() => del(c.id))} className="p-2 hover:bg-zinc-100 rounded-lg text-rose-400" title="Elimina" data-testid={`delete-${c.id}`}><Trash2 size={15} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Editor multi-card ──────────────────────────────────────────────────────
function CustomerEditor({ form, setForm, editingId, busy, save, cancel, geocodeNow }) {
  const biz = isBusiness(form.type);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="customer-editor">
      <PageHeader eyebrow={editingId ? "Modifica" : "Nuovo"} title={editingId ? "Modifica cliente" : "Nuovo cliente"}
        subtitle={biz ? "Profilo azienda · ammortamento e bonus a fondo perduto" : "Profilo privato · detrazioni fiscali IRPEF"}
        actions={
          <div className="flex gap-2">
            <button onClick={cancel} className="px-3 py-2 border border-zinc-200/70 rounded-lg text-sm flex items-center gap-1.5"><ArrowLeft size={13}/> Clienti</button>
            <button onClick={save} disabled={busy} className="px-4 py-2 bg-zinc-900 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50" data-testid="customer-save-btn">
              <Check size={14}/> {busy ? "Salvataggio…" : (editingId ? "Salva modifiche" : "Crea cliente")}
            </button>
          </div>
        }/>

      <div className="px-8 py-6">
        {/* Selettore profilo */}
        <div className="bg-white border border-zinc-200/70 rounded-2xl p-2 mb-6 inline-flex flex-wrap gap-1" data-testid="customer-type-switch">
          {Object.entries(TYPE_LABELS).map(([val, lbl]) => {
            const active = form.type === val;
            const Ic = val === "private" ? User : Building2;
            return (
              <button key={val} onClick={() => set("type", val)}
                className={`px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${active ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}
                data-testid={`type-${val}`}>
                <Ic size={15}/> {lbl}
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-7 items-start">
          {/* Card 1 — Anagrafica (varia per profilo) */}
          <Card icon={biz ? Building2 : User} accent={biz ? "sky" : "amber"} eyebrow={biz ? "Anagrafica azienda" : "Anagrafica privato"} title={biz ? "Dati dell'azienda" : "Dati del cliente"}>
            {biz ? (
              <Field label="Ragione sociale">
                <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} data-testid="customer-business" placeholder="Es. Officina Verde SRL" />
              </Field>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nome"><input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} data-testid="customer-firstname" /></Field>
                <Field label="Cognome"><input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputCls} data-testid="customer-lastname" /></Field>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><input value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="customer-email" /></Field>
              <Field label="Telefono"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="customer-phone" /></Field>
            </div>
            <Field label="Stato pipeline">
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="customer-status">
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </Card>

          {/* Card 2 — Indirizzo & posizione */}
          <Card icon={MapPin} accent="sky" eyebrow="Indirizzo di installazione" title="Dove installiamo">
            <Field label="Indirizzo"><input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="customer-address" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Field label="Città"><input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></Field></div>
              <Field label="PR"><input value={form.province} onChange={(e) => set("province", e.target.value)} className={inputCls} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="CAP"><input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className={inputCls} /></Field>
              <Field label="Lat"><input type="number" step="0.0001" value={form.latitude ?? ""} onChange={(e) => set("latitude", parseFloat(e.target.value) || null)} className={inputCls} /></Field>
              <Field label="Lng"><input type="number" step="0.0001" value={form.longitude ?? ""} onChange={(e) => set("longitude", parseFloat(e.target.value) || null)} className={inputCls} /></Field>
            </div>
            <button type="button" onClick={geocodeNow} className="w-full text-xs text-sky-600 hover:text-amber-600 border border-zinc-200/70 rounded-lg py-2 flex items-center justify-center gap-1.5" data-testid="customer-geocode-btn">
              <MapPin size={12}/> Trova coordinate da indirizzo (Mapbox)
            </button>
          </Card>

          {/* Card 3 — Consumo & contatore */}
          <Card icon={Zap} accent="emerald" eyebrow="Consumo & contatore" title="Profilo energetico">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Consumo annuo kWh"><input type="number" value={form.annual_consumption_kwh} onChange={(e) => set("annual_consumption_kwh", parseFloat(e.target.value) || 0)} className={inputCls} data-testid="customer-consumption" /></Field>
              <Field label="Prezzo €/kWh"><input type="number" step="0.01" value={form.energy_price_per_kwh} onChange={(e) => set("energy_price_per_kwh", parseFloat(e.target.value) || 0)} className={inputCls} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Potenza contatore kW"><input type="number" step="0.5" value={form.meter_power_kw} onChange={(e) => set("meter_power_kw", parseFloat(e.target.value) || 0)} className={inputCls} data-testid="customer-meter-power" /></Field>
              <Field label="Fase contatore">
                <select value={form.meter_phase} onChange={(e) => set("meter_phase", e.target.value)} className={inputCls} data-testid="customer-meter-phase">
                  <option value="monofase">Monofase</option>
                  <option value="trifase">Trifase</option>
                </select>
              </Field>
            </div>
            <div className="flex gap-2 pt-1">
              <Toggle on={form.interest_battery} onClick={() => set("interest_battery", !form.interest_battery)} label="Interesse batteria" />
              <Toggle on={form.interest_ev} onClick={() => set("interest_ev", !form.interest_ev)} label="Interesse ricarica EV" />
            </div>
          </Card>

          {/* Card 4 — Detrazioni & agevolazioni (zona dedicata) */}
          <IncentivesCard form={form} setForm={setForm} biz={biz} />
        </div>
      </div>
    </div>
  );
}

// ── Zona detrazioni / agevolazioni ──────────────────────────────────────────
function IncentivesCard({ form, setForm, biz }) {
  const incentives = form.incentives || [];
  const setInc = (arr) => setForm((f) => ({ ...f, incentives: arr }));
  const add = (preset) => setInc([...incentives, { id: newId(), enabled: true, kind: "detrazione", pct: 0, years: 10, label: "Nuova detrazione", ...preset }]);
  const upd = (id, k, v) => setInc(incentives.map((i) => i.id === id ? { ...i, [k]: v } : i));
  const remove = (id) => setInc(incentives.filter((i) => i.id !== id));
  const presets = biz ? PRESETS_BUSINESS : PRESETS_PRIVATE;
  const enabledCount = incentives.filter((i) => i.enabled).length;

  return (
    <Card icon={Percent} accent="amber" eyebrow="Detrazioni & agevolazioni" title="Bonus e detrazioni"
      headerRight={<span className="font-mono text-[10px] text-zinc-400">{enabledCount} attive</span>}>
      <p className="text-xs text-zinc-500 -mt-1">Aggiungi le agevolazioni applicabili e attiva/disattiva quelle che vuoi includere nel business plan.</p>

      <div className="flex flex-wrap gap-1.5">
        {presets.map((p) => (
          <button key={p.label} type="button" onClick={() => add(p)} className="text-[11px] px-2.5 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200/70 hover:bg-amber-100 flex items-center gap-1" data-testid={`preset-${p.kind}`}>
            <Plus size={11}/> {p.label}
          </button>
        ))}
        <button type="button" onClick={() => add({ label: "Personalizzata", kind: biz ? "fondo_perduto" : "detrazione" })} className="text-[11px] px-2.5 py-1.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200 flex items-center gap-1" data-testid="preset-custom">
          <Plus size={11}/> Personalizzata
        </button>
      </div>

      <div className="space-y-2">
        {incentives.length === 0 && (
          <div className="text-center text-xs text-zinc-400 py-6 border border-dashed border-zinc-200 rounded-xl flex flex-col items-center gap-1">
            <Sparkles size={16} className="text-amber-400"/> Nessuna detrazione. Aggiungine una dai pulsanti sopra.
          </div>
        )}
        {incentives.map((i) => (
          <div key={i.id} className={`rounded-xl border p-3 transition-colors ${i.enabled ? "bg-white border-zinc-200" : "bg-zinc-50 border-zinc-200/60 opacity-70"}`} data-testid="incentive-row">
            <div className="flex items-center gap-2">
              <Switch on={!!i.enabled} onClick={() => upd(i.id, "enabled", !i.enabled)} />
              <input value={i.label} onChange={(e) => upd(i.id, "label", e.target.value)} className="flex-1 bg-transparent border-0 border-b border-transparent hover:border-zinc-200 focus:border-amber-400 px-1 py-0.5 text-sm font-medium outline-none" placeholder="Nome agevolazione" />
              <button onClick={() => remove(i.id)} className="text-zinc-300 hover:text-rose-500 p-1"><Trash2 size={13}/></button>
            </div>
            <div className="flex items-end gap-2 mt-2 pl-9">
              <div className="flex-1">
                <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400">Tipo</label>
                <select value={i.kind} onChange={(e) => upd(i.id, "kind", e.target.value)} className="w-full mt-0.5 bg-zinc-50 border border-zinc-200/70 rounded-lg px-2 py-1.5 text-xs">
                  {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {i.kind === "importo" ? (
                <div className="w-24">
                  <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400">Importo €</label>
                  <input type="number" value={i.amount ?? ""} onChange={(e) => upd(i.id, "amount", parseFloat(e.target.value) || 0)} className="w-full mt-0.5 bg-zinc-50 border border-zinc-200/70 rounded-lg px-2 py-1.5 text-xs font-mono text-right" />
                </div>
              ) : (
                <div className="w-20">
                  <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400">%</label>
                  <input type="number" value={i.pct ?? ""} onChange={(e) => upd(i.id, "pct", parseFloat(e.target.value) || 0)} className="w-full mt-0.5 bg-zinc-50 border border-zinc-200/70 rounded-lg px-2 py-1.5 text-xs font-mono text-right" />
                </div>
              )}
              {i.kind === "detrazione" && (
                <div className="w-20">
                  <label className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400">Anni</label>
                  <input type="number" value={i.years ?? 10} onChange={(e) => upd(i.id, "years", parseInt(e.target.value, 10) || 10)} className="w-full mt-0.5 bg-zinc-50 border border-zinc-200/70 rounded-lg px-2 py-1.5 text-xs font-mono text-right" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {biz && (
        <Field label="Ammortamento fiscale (anni)">
          <input type="number" min="0" max="20" value={form.amortization_years} onChange={(e) => setForm((f) => ({ ...f, amortization_years: parseInt(e.target.value, 10) || 0 }))} className={inputCls} data-testid="customer-amortization" />
        </Field>
      )}
    </Card>
  );
}

// ── UI helpers ──────────────────────────────────────────────────────────────
const inputCls = "w-full bg-zinc-50 border border-zinc-200/70 rounded-xl px-3.5 py-3 text-sm focus:border-amber-300 outline-none";
const ACCENTS = {
  amber: "bg-amber-50 text-amber-600 border-amber-200",
  sky: "bg-sky-50 text-sky-600 border-sky-200",
  emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
};

function Card({ icon: Ic, accent = "amber", eyebrow, title, headerRight, children }) {
  return (
    <div className="bg-white border border-zinc-200/70 rounded-3xl p-7 space-y-5 self-start">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-2xl border flex items-center justify-center ${ACCENTS[accent]}`}><Ic size={19}/></div>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{eyebrow}</div>
          <div className="font-semibold text-zinc-900 leading-tight text-[15px]">{title}</div>
        </div>
        {headerRight}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ on, onClick, label }) {
  return (
    <button type="button" onClick={onClick} className={`flex-1 rounded-xl px-3 py-2 text-xs font-medium border transition-colors flex items-center justify-center gap-1.5 ${on ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-zinc-50 text-zinc-500 border-zinc-200"}`}>
      {on && <Check size={12}/>} {label}
    </button>
  );
}

function Switch({ on, onClick }) {
  return (
    <button type="button" onClick={onClick} role="switch" aria-checked={on} data-testid="incentive-toggle"
      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${on ? "bg-amber-400" : "bg-zinc-300"}`}>
      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
    </button>
  );
}
